<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { useAlert } from 'dashboard/composables';
import { useFacebookAdsLogin } from 'dashboard/composables/useFacebookAdsLogin';
import { useMetaCampaignsAdAccountsStore } from 'dashboard/stores/metaCampaignsAdAccounts';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['connected']);

const { t } = useI18n();
const adAccountsStore = useMetaCampaignsAdAccountsStore();
const { login } = useFacebookAdsLogin();

const dialogRef = ref(null);
const isAuthenticating = ref(false);
const isConnecting = ref(false);
const hasError = ref(false);
const errorMessage = ref('');
const availableAdAccounts = ref([]);
const longLivedToken = ref('');
const showManualEntry = ref(false);
const manualToken = ref('');

const alreadyConnectedIds = computed(() =>
  adAccountsStore.getAdAccounts.map(record => record.meta_ad_account_id)
);

const resetState = () => {
  isAuthenticating.value = false;
  isConnecting.value = false;
  hasError.value = false;
  errorMessage.value = '';
  availableAdAccounts.value = [];
  longLivedToken.value = '';
  showManualEntry.value = false;
  manualToken.value = '';
};

const extractErrorMessage = error =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  t('META_CAMPAIGNS.CONNECT_MODAL.ERROR');

const startLogin = async () => {
  hasError.value = false;
  isAuthenticating.value = true;
  try {
    const shortLivedToken = await login();
    const { access_token: token, ad_accounts: adAccounts } =
      await adAccountsStore.oauthAdAccounts(shortLivedToken);
    longLivedToken.value = token;
    availableAdAccounts.value = adAccounts;
  } catch (error) {
    Sentry.captureException(error);
    hasError.value = true;
    errorMessage.value = extractErrorMessage(error);
  } finally {
    isAuthenticating.value = false;
  }
};

const lookupManually = async () => {
  if (!manualToken.value.trim()) return;

  hasError.value = false;
  isAuthenticating.value = true;
  try {
    const { access_token: token, ad_accounts: adAccounts } =
      await adAccountsStore.lookupAdAccounts(manualToken.value.trim());
    longLivedToken.value = token;
    availableAdAccounts.value = adAccounts;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = extractErrorMessage(error);
  } finally {
    isAuthenticating.value = false;
  }
};

const connectAdAccount = async adAccount => {
  isConnecting.value = true;
  try {
    await adAccountsStore.connect({
      meta_ad_account_id: adAccount.id.replace('act_', ''),
      access_token: longLivedToken.value,
      name: adAccount.name,
      currency: adAccount.currency,
      timezone_name: adAccount.timezone_name,
    });
    useAlert(t('META_CAMPAIGNS.CONNECT_MODAL.SUCCESS'));
    availableAdAccounts.value = availableAdAccounts.value.filter(
      a => a.id !== adAccount.id
    );
    emit('connected');
  } catch (error) {
    useAlert(extractErrorMessage(error));
  } finally {
    isConnecting.value = false;
  }
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('META_CAMPAIGNS.CONNECT_MODAL.TITLE')"
    :description="t('META_CAMPAIGNS.CONNECT_MODAL.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('META_CAMPAIGNS.CONNECT_MODAL.CLOSE')"
    @close="resetState"
  >
    <div class="flex flex-col gap-4">
      <template v-if="!availableAdAccounts.length">
        <Button
          :label="t('META_CAMPAIGNS.CONNECT_MODAL.LOGIN_BUTTON')"
          :is-loading="isAuthenticating"
          icon="i-lucide-facebook"
          @click="startLogin"
        />

        <button
          type="button"
          class="text-xs text-n-slate-10 hover:text-n-slate-12 underline self-start"
          @click="showManualEntry = !showManualEntry"
        >
          {{ t('META_CAMPAIGNS.CONNECT_MODAL.MANUAL_TOGGLE') }}
        </button>

        <div v-if="showManualEntry" class="flex flex-col gap-2">
          <Input
            v-model="manualToken"
            :placeholder="t('META_CAMPAIGNS.CONNECT_MODAL.MANUAL_PLACEHOLDER')"
          />
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="t('META_CAMPAIGNS.CONNECT_MODAL.MANUAL_SUBMIT')"
            :is-loading="isAuthenticating"
            :disabled="!manualToken.trim()"
            @click="lookupManually"
          />
        </div>
      </template>

      <p v-if="hasError" class="text-sm text-n-ruby-9">
        {{ errorMessage || t('META_CAMPAIGNS.CONNECT_MODAL.ERROR') }}
      </p>

      <ul v-if="availableAdAccounts.length" class="flex flex-col gap-2">
        <li
          v-for="adAccount in availableAdAccounts"
          :key="adAccount.id"
          class="flex items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              adAccount.name
            }}</span>
            <span class="text-xs text-n-slate-10"
              >{{ adAccount.id }} · {{ adAccount.currency }}</span
            >
          </div>
          <Button
            size="sm"
            :label="
              alreadyConnectedIds.includes(adAccount.id.replace('act_', ''))
                ? t('META_CAMPAIGNS.CONNECT_MODAL.CONNECTED')
                : t('META_CAMPAIGNS.CONNECT_MODAL.CONNECT')
            "
            :disabled="
              isConnecting ||
              alreadyConnectedIds.includes(adAccount.id.replace('act_', ''))
            "
            :is-loading="isConnecting"
            @click="connectAdAccount(adAccount)"
          />
        </li>
      </ul>
    </div>

    <template #footer>
      <div class="flex justify-end w-full">
        <Button
          :label="t('META_CAMPAIGNS.CONNECT_MODAL.CLOSE')"
          variant="faded"
          color="slate"
          @click="close"
        />
      </div>
    </template>
  </Dialog>
</template>
